import React, { useEffect, useState, useCallback } from 'react';
import cx from 'classnames';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { Row, Col } from 'react-bootstrap';
import Button from '@/components/Button';
import MetaHead from '@/components/MetaHead';
import { Search } from '@/components/Search';
import { useDrugSearchResult } from '@/hooks/farmasep';
import { useTranslation } from '@/hooks/translation';
import { useCurrentUser } from '@/hooks/user';
import { csLoader } from '@/utilities/image';
import LoginModal from '@separate/components/LoginModal';
import styles from './farmasep.module.scss';

export default function FarmasepPage() {
  const { t } = useTranslation();
  const router = useRouter();
  const { user, isLoggedIn } = useCurrentUser();
  const imageUrl = 'https://images.contentstack.io/v3/assets/bltd488044897c9abc0/blt4283c691d5d63ba7/5f5fa74521ecf947895d995b/header.jpg';
  const { replace } = useRouter();

  const [popularSearch, setPopularSearch] = useState('');
  const [searchZipCode, setSearchZipCode] = useState('');
  const [autoComplete, setAutoComplete] = useState([]);
  const [disabled, setDisabled] = useState(true);
  const [showLogin, setShowLogin] = useState(false);

  // Hide the FarmaSep page until the third-party API is fixed
  React.useEffect(() => {
    replace('/');
  }, [replace]);

  const callDrugSearch = useDrugSearchResult(popularSearch, searchZipCode);

  const popularSearchItems = [
    ...t('farmasep.popularSeaches.drug', { returnObjects: true }),
  ];

  const retailers = [...t('farmasep.retailers', { returnObjects: true })];

  useEffect(() => {
    if (popularSearch.trim().length > 0 && searchZipCode.trim().length == 5) {
      if (isNaN(searchZipCode)) {
        setDisabled(true);
      } else {
        setDisabled(false);
      }
    } else {
      setDisabled(true);
    }
  }, [popularSearch, searchZipCode]);

  useEffect(() => {
    setSearchZipCode(user?.zipcode ? user.zipcode : '');
  }, [user]);

  useEffect(() => {
    setTimeout(() => {
      setAutoComplete(callDrugSearch.data?.data?.data?.candidates);
    }, 250);
  }, [callDrugSearch]);

  const handleResultRoute = useCallback((searchString, zipCode) => {
    const queryString = `drug=${searchString.toLocaleLowerCase()}&zip=${zipCode}`;
    router.push(`/farmasep/result?${queryString}`);
  }, [router]);

  function handleSearch(searchString = popularSearch) {
    if (!isLoggedIn) {
      setShowLogin(true);
      setPopularSearch(searchString);
    } else {
      handleResultRoute(searchString, searchZipCode);
    }
  }

  function hideLogin () {
    setShowLogin(false);
  }

  function handleOnSubmit() {
    if (!disabled) {
      handleSearch();
    }
  }

  function handlePopularSearchClick(item) {
    if (searchZipCode.trim().length == 5 && !isNaN(searchZipCode)) {
      handleSearch(item);
    } else {
      setPopularSearch(item);
    }
  }

  return (
    <div className={styles.wrapper}>
      <MetaHead
        title={t('meta.farmasep.title')}
        description={t('meta.farmasep.description')}
        imageUrl={imageUrl}
        imageType='image/jpeg'
        imageWidth='750'
        imageHeight='220'
      />
      <Row>
        <Col className={styles.header}>
          <div className={styles.headerImage}>
            <p className={styles.slogan}>{t('farmasep.main.title')}</p>
          </div>
        </Col>
      </Row>

      <Row className='mt-3'>
        <Col>
          <div>
            <p className={cx(styles.title, 'mb-1')}>
              {t('farmasep.form.drugName')}
            </p>
            <Search
              type="text"
              handleOnSubmit={handleOnSubmit}
              placeholder={t('farmasep.form.drugPlaceholder')}
              onChange={setPopularSearch}
              value={popularSearch}
              autoComplete={autoComplete}
            />
          </div>
        </Col>
      </Row>

      <Row className='mt-4'>
        <Col>
          <div>
            <p className={cx(styles.title, 'mb-1')}>
              {t('farmasep.content.popular')}
            </p>

            <div className={styles.popularSearchContainer}>
              {popularSearchItems.map((item, index) => (
                <div
                  key={index}
                  className={styles.popularSearchItem}
                  onClick={() => handlePopularSearchClick(item.drugName)}
                >
                  <p> {item.drugName} </p>
                </div>
              ))}
            </div>
          </div>
        </Col>
      </Row>

      <Row className='mt-4'>
        <Col>
          <div className={styles.zipCodeContainer}>
            <p className={cx(styles.title, 'mb-1')}>
              {t('farmasep.form.zipCode')}
            </p>
            <Search
              type="tel"
              placeholder={t('farmasep.form.zipCode')}
              className='text-center'
              onChange={setSearchZipCode}
              value={searchZipCode}
              handleOnSubmit={handleOnSubmit}
              mask="99999"
            />
          </div>
        </Col>
      </Row>

      <Row className='mt-4'>
        <Col>
          <div>
            <Button
              className='w-100'
              disabled={disabled}
              onClick={handleSearch}
            >
              {t('farmasep.form.search')}
            </Button>
          </div>
        </Col>
      </Row>

      <Row className='mt-4'>
        <Col>
          <div className='text-center'>
            <p className={cx(styles.title, 'mb-1')}>
              {t('farmasep.content.drugstores')}
            </p>
            <div className={styles.retailerContainer}>
              {retailers.map((item, index) => (
                <div key={index} className={styles.retailerLogo}>
                  <Image
                    src={item.image}
                    alt='avatar'
                    loader={csLoader}
                    height={65}
                    width={50}
                  />
                </div>
              ))}
            </div>
          </div>
        </Col>
      </Row>

      <LoginModal show={showLogin} onHide={hideLogin} onLoginSuccess={() => handleResultRoute(popularSearch, searchZipCode)} />
    </div>
  );
}
